import React, { useState, useEffect } from "react";
import api from "../../api/api";
import TransferList from "../TransferList";
import { useForm, useFieldArray } from "react-hook-form";
import { Button } from "@mui/material";
import { CloseOutlined, Add, Save, ArrowBack } from "@mui/icons-material";

const Edit = ({ setCards, setWarning, handleOpenSnackbar, item }) => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      categoryId: item.categoryId,
      viewProducts: item.viewProducts,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "viewProducts",
  });

  const addCard = () => {
    if (fields.length < 2) {
      append({ name: "", href: "", imgSrc: "", imgAlt: "", actionText: "" });
    }
  };

  const onSubmit = (formData) => {
    const requestData = {
      viewProducts: formData.viewProducts.map((product) => ({
        ...product,
        href: `/produtos?category=${product.href}`,
      })),
      categoryId: formData.categoryId,
    };

    api
      .post(`/categoriesFeatured`, requestData)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          setWarning({
            type: "success",
            message: "Destaque editado com sucesso!",
          });
          handleOpenSnackbar();
          setCards("");
        } else {
          setWarning({
            type: "warning",
            message: "Falha ao editar o destaque.",
          });
          handleOpenSnackbar();
        }
      })
      .catch((error) => {
        let errorMessage = "Falha ao editar o destaque.";
        if (error.response) {
          errorMessage += ` Detalhes: ${
            error.response.data.message || error.response.status
          }`;
        } else if (error.request) {
          errorMessage = "O servidor não respondeu ao pedido.";
        } else {
          errorMessage = "Erro ao enviar o pedido para o servidor.";
        }
        setWarning({
          type: "error",
          message: errorMessage,
        });
        handleOpenSnackbar();
      });
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setCards("")}
            className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowBack className="w-6 h-6" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Editar Destaque</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Formulário - Lado Esquerdo */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Configurações Gerais
              </h3>

              <div className="mb-4">
                <label
                  htmlFor="categoryId"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  ID da Categoria *
                </label>
                <input
                  {...register("categoryId", {
                    required: "ID da categoria é obrigatório",
                  })}
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Ex: 65eb23aa360e2d56bf94aa24"
                />
                {errors.categoryId && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.categoryId.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Produtos em Destaque ({fields.length}/2)
              </h3>

              {fields.map((item, index) => (
                <div
                  key={item.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-800">
                      Produto {index + 1}
                    </h4>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <CloseOutlined className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome do Produto *
                      </label>
                      <input
                        {...register(`viewProducts.${index}.name`, {
                          required: "Nome do produto é obrigatório",
                        })}
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Ex: Linha SOS"
                      />
                      {errors.viewProducts?.[index]?.name && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.viewProducts[index].name.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ID da Categoria (Link) *
                      </label>
                      <input
                        {...register(`viewProducts.${index}.href`, {
                          required: "ID da categoria é obrigatório",
                        })}
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Ex: 65e8a436b56b665eb5255e7b"
                      />
                      {errors.viewProducts?.[index]?.href && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.viewProducts[index].href.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        URL da Imagem *
                      </label>
                      <input
                        {...register(`viewProducts.${index}.imgSrc`, {
                          required: "URL da imagem é obrigatória",
                        })}
                        type="url"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Ex: https://i.imgur.com/mDVlxI2.png"
                      />
                      {errors.viewProducts?.[index]?.imgSrc && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.viewProducts[index].imgSrc.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descrição da Imagem *
                      </label>
                      <input
                        {...register(`viewProducts.${index}.imgAlt`, {
                          required: "Descrição da imagem é obrigatória",
                        })}
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Descreva a imagem para acessibilidade"
                      />
                      {errors.viewProducts?.[index]?.imgAlt && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.viewProducts[index].imgAlt.message}
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Texto de Ação *
                      </label>
                      <input
                        {...register(`viewProducts.${index}.actionText`, {
                          required: "Texto de ação é obrigatório",
                        })}
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Ex: Veja todos os produtos desta categoria..."
                      />
                      {errors.viewProducts?.[index]?.actionText && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.viewProducts[index].actionText.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <div className="mt-3">
                {fields.length < 2 && (
                  <button
                    type="button"
                    onClick={addCard}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Add className="w-5 h-5" />
                    Adicionar Produto
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <button
                type="button"
                onClick={() => setCards("")}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                Salvar Alterações
              </button>
            </div>
          </form>
        </div>

        {/* Caixa de Alerta - Lado Direito */}
        <div className="lg:col-span-1">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 sticky top-6">
            <h2 className="text-xl font-semibold text-yellow-800 mb-3">
              Editando Destaque Existente
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-yellow-700">
                  <strong>Categoria ID:</strong> {item.categoryId}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-yellow-700">
                  <strong>Produtos:</strong> {item.viewProducts?.length || 0}{" "}
                  produtos configurados
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Edit;
